/*
 * Nosto Search JS
 * Handles Nosto-powered search and category pages, including facets,
 * pagination, and product rendering.
 */

class NostoSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.searchTerm = '';
    this.searchableFacets = ['brand'];
    this.openedFacets = [];
    this.activeMaxPrice = null;
    this.lastEditedFacet = null;
    this.includeFacetRules = { 'beauty': 'sub-category' };
    this.excludeFacets = [];
    this.usingFallback = false;
    this.fallbackAttemptTime = null;
    this.retryCount = 0;
    this.maxRetries = 3;
  }

  connectedCallback() {
    this.options = JSON.parse(this.getAttribute('data-options') || '{}');
    this.maxProducts = this.options.maxProducts ? parseInt(this.options.maxProducts) : null;
    this.loadingSpinner = this.querySelector('.loading-overlay');
    this.productGrid = this.querySelector('[data-product-grid-nosto]');
    this.container = this;

    this.gridySlider = this.querySelector('gridy-slider');

    this.iprDesktop = this.options.iprDesktop ?
      parseInt(this.options.iprDesktop) :
      (this.gridySlider ? parseInt(this.gridySlider.dataset.iprDesktop) : null);

    this.iprTablet = this.options.iprTablet ?
      parseInt(this.options.iprTablet) :
      (this.gridySlider ? parseInt(this.gridySlider.dataset.iprTablet) : null);

    this.iprMobile = this.options.iprMobile ?
      parseInt(this.options.iprMobile) :
      (this.gridySlider ? parseInt(this.gridySlider.dataset.iprMobile) : null);

    this.arrows = this.querySelector('.gridy-arrows');

    this.searchForm = this.querySelector('form[role="search"]');
    this.input = this.querySelector('input[type="search"]');
    this.resultsCount = this.querySelector('#products-count-ajax');
    this.header = this.querySelector('header h1');
    this.facetsFormContainer = document.querySelector('.filter-form');
    this.abortController = new AbortController();
    this.sortBy = this.querySelector('#sort-by');
    this.viewMoreButton = this.querySelector('#pagination-ajax button');
    this.showCount = this.querySelector('[data-show-count]');
    this.resultsSize = parseInt(this.productGrid?.getAttribute('data-results-per-page')) || 24;
    this.context = this.productGrid?.getAttribute('data-context');
    this.collectionId = this.productGrid?.getAttribute('data-collection-id') || null;
    this.collectionTitle = this.productGrid?.getAttribute('data-collection') || null;

    if (this.context === 'serp') {
      this.excludeFacets.push('sub-category');
    }

    this.enableFallback = JSON.parse(this.options.enableFallback || 'false');

    if (!this.productGrid) {
      console.error('Nosto: Required product grid element not found');
      return;
    }

    this.setLoadingState();
    this.setupClickTracking();
    this.setupEventListeners();
    this.initializeFromURL();
  }

  /**
   * Generate HTML markup for products returned from Nosto API
   * @param {Array} products - Array of product data from Nosto
   * @returns {Promise<string>} HTML markup for the product grid
   */
  async generateProductsMarkup(products) {
    if (!products || !Array.isArray(products)) {
      console.warn('Invalid products data provided to generateProductsMarkup');
      return '';
    }

    // Get template content
    const template = document.querySelector('.js-card-product-template');
    if (!template) {
      console.warn('Product card template not found');
      return '';
    }

    const productsMarkup = await Promise.all(products.map(async (product, index) => {
      if (!product) return '';

      const templateContent = template.content.cloneNode(true);
      const container = document.createElement('div');
      container.appendChild(templateContent);

      try {
        // Extract product data with fallbacks
        const productHandle = product.url?.split('/').slice(-1)[0] || '';
        const productUrlRelative = productHandle ? `/products/${productHandle}` : '#';
        const productPrice = product.price ? product.price : '';
        const productPriceWithFormatting = productPrice ? Woolman.Utils.formatMoney(productPrice * 100) : '';
        const productListPrice = product.listPrice || product.list_price || '';
        const productListPriceWithFormatting = productListPrice ? Woolman.Utils.formatMoney(productListPrice * 100) : '';
        const onDiscount = productListPrice && productPrice < productListPrice;
        const productThumbUrl = product.imageUrl || product.image_url || '';
        const productAltThumbUrls = product.alternateImageUrls || product.alternate_image_urls || '';
        const productName = product.name || product.product_name || 'Product';
        const productBrand = product.brand || product.vendor || '';
        const productId = product.productId || product.product_id || '';
        const variantId = product.skus[0]?.id || null;
        const variantIds = product.skus ? product.skus.map(sku => sku.id) : [];
        const customFields = product.customFields || product.custom_fields || {};

        // Replace placeholders
        container.innerHTML = container.innerHTML
          .replace(/\[\[product\.url\]\]/g, productUrlRelative)
          .replace(/\[\[product\.id\]\]/g, productId)
          .replace(/\[\[product\.handle\]\]/g, productHandle);

        const cardType = this.options.cardType;
        const cardTextAlign = this.options.cardTextAlign;

        const cardElement = container.querySelector('.card--product');
        cardElement.setAttribute('data-index', index + 1);
        if (cardType) {
          cardElement.classList.add(`card--${cardType}`);
        }
        if (cardTextAlign) {
          cardElement.querySelector('.card-body').classList.add(cardTextAlign);
        }
        const productDataSpan = document.createElement('span');
        productDataSpan.className = 'hidden';
        productDataSpan.setAttribute('data-product-json', productId);
        productDataSpan.innerHTML = JSON.stringify(product);
        cardElement.appendChild(productDataSpan);

        const productAriaLabel = window.accessibilityStrings.viewSomething.replace('{{ something }}', productName);
        container.querySelector('.card-media').setAttribute('aria-label', productAriaLabel);
        container.querySelector('.card-title a').setAttribute('aria-label', productAriaLabel);

        // Set primary image for normal product cards
        this._setProductImage(container, productThumbUrl, productName, index, productAltThumbUrls);

        // Set badges
        this._setProductBadges(container, customFields);

        // Set product title
        const titleElement = container.querySelector('[data-product-title]');
        if (titleElement) {
          titleElement.textContent = productName;
        }

        // Set vendor/brand
        const vendorElement = container.querySelector('[data-vendor]');
        if (vendorElement && productBrand) {
          vendorElement.textContent = productBrand;
        }

        // Set price
        this._setProductPrice(container, productPriceWithFormatting, productListPriceWithFormatting, onDiscount, productBrand, product);

        // Store product data for deferred swatch loading
        // This prevents blocking the initial render while still allowing swatches to load later
        const swatchesContainer = container.querySelector('[data-product-swatches]');
        if (swatchesContainer) {
          swatchesContainer.setAttribute('data-product-handle', productHandle);
          swatchesContainer.setAttribute('data-custom-fields', JSON.stringify(customFields));
        }

        // Add fade-in class with staggered delay
        if (cardElement) {
          // Add a small delay based on index for staggered animation
          cardElement.style.animationDelay = `${index * 0.05}s`;
          cardElement.classList.add('fade-in');
          cardElement.setAttribute("data-track-click", "product_card_click");
          cardElement.setAttribute("data-track-product-id", productId);
        }

        return container.innerHTML;
      } catch (error) {
        console.error('Error processing product markup:', error);
        return '';
      }
    }));

    // Handle arrows visibility based on product count
    this._handleArrowsVisibility(products);

    return productsMarkup.join('');
  }

  /**
   * Load swatches for all product cards after initial render
   * This method is called after the product grid is updated with new cards
   * Uses Intersection Observer to load swatches only when cards are visible
   */
  async loadDeferredSwatches() {
    const swatchContainers = this.productGrid.querySelectorAll('[data-product-swatches][data-product-handle]');

    if (!swatchContainers.length) return;

    // Use Intersection Observer to load swatches only when they're near the viewport
    const observerOptions = {
      root: null,
      rootMargin: '200px', // Start loading 200px before entering viewport
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const container = entry.target;
          this._loadSingleCardSwatches(container);
          observer.unobserve(container); // Stop observing once loaded
        }
      });
    }, observerOptions);

    // Observe all swatch containers
    swatchContainers.forEach(container => observer.observe(container));
  }

  /**
   * Load swatches for a single product card
   * @param {HTMLElement} swatchContainer - The swatch container element
   */
  async _loadSingleCardSwatches(swatchContainer) {
    try {
      const productHandle = swatchContainer.getAttribute('data-product-handle');
      const customFieldsStr = swatchContainer.getAttribute('data-custom-fields');

      if (!productHandle) return;

      const customFields = customFieldsStr ? JSON.parse(customFieldsStr) : {};

      // Get the product card container for hover functionality
      const productCard = swatchContainer.closest('.card--product');
      const productData = productCard?.querySelector('[data-product-json]');
      const product = productData ? JSON.parse(productData.textContent) : null;

      if (!product || !productCard) return;

      // Call the existing swatch generation method, passing the product card as container
      await this._setProductSwatches(productCard, product, customFields, swatchContainer);

      // Clean up data attributes after loading
      swatchContainer.removeAttribute('data-product-handle');
      swatchContainer.removeAttribute('data-custom-fields');
    } catch (error) {
      console.error('Error loading deferred swatches:', error);
    }
  }

  /**
   * Helper method to set product image
   */
  _setProductImage(container, thumbUrl, productName, index, alternateImageUrls) {
    const primaryImageContainer = container.querySelector('.card-media');
    if (primaryImageContainer && thumbUrl) {
      const cardImgAspectRatio = this.options.cardImgAspectRatio;
      const cardImgSizingRule = this.options.cardImgSizingRule;

      // Determine loading strategy:
      // 1. Always use lazy loading for all images in gridySlider
      // 2. If no slider is used, load the first visible products eagerly based on iprDesktop value
      // 3. Default to loading first 4 products eagerly if iprDesktop is not available
      let loadingStrategy = 'lazy';
      let fetchPriority = 'low';

      if (!this.gridySlider) {
        // Use iprDesktop value to determine how many products to load eagerly
        // This matches the number of products visible in the first row
        // Use iprDesktop for desktop, iprMobile for mobile, fallback to 4
        let visibleItemsCount;
        if (window.matchMedia && window.matchMedia('(max-width: 767px)').matches) {
          visibleItemsCount = this.iprMobile || 2;
        } else {
          visibleItemsCount = this.iprDesktop || 4;
        }
        loadingStrategy = index < visibleItemsCount ? 'eager' : 'lazy';
        fetchPriority = index < visibleItemsCount ? 'high' : 'low';
      }

      // Create wrapper div for hover images only if hover functionality is enabled
      // Otherwise, add the primary image directly to the container
      if (alternateImageUrls && alternateImageUrls.length > 0 && this.options.showSecondImageOnHover) {
        const hoverImagesDiv = document.createElement('div');
        hoverImagesDiv.className = 'card-hover-images';

        // Create primary image figure
        const primaryFigureElement = document.createElement('figure');
        primaryFigureElement.className = `media ${cardImgSizingRule} ${cardImgAspectRatio}`;

        // Create srcset with multiple resolutions for primary image
        const primarySrcset = [
          `${thumbUrl}&width=90 90w`,
          `${thumbUrl}&width=180 180w`,
          `${thumbUrl}&width=280 280w`,
          `${thumbUrl}&width=390 390w`,
          `${thumbUrl}&width=540 540w`,
          `${thumbUrl}&width=768 768w`,
          `${thumbUrl}&width=1080 1080w`,
          `${thumbUrl}&width=1280 1280w`,
          `${thumbUrl}&width=1500 1500w`
        ].join(', ');

        primaryFigureElement.innerHTML = `
              <img
                srcset="${primarySrcset}"
                sizes="100vw"
                src="${thumbUrl}"
                alt="${productName}"
                loading="${loadingStrategy}"
                fetchpriority="${fetchPriority}"
                decoding="async"
                onload="this.classList.add('loaded')"
              >
            `;

        // Add primary image to hover wrapper
        hoverImagesDiv.appendChild(primaryFigureElement);

        // Add the on-hover class to product card
        const productCard = container.querySelector('.card--product');
        productCard.classList.add('on-hover');

        // Add alternate image
        const alternateImageUrl = alternateImageUrls[0];
        const alternateFigureElement = document.createElement('figure');
        alternateFigureElement.className = `media ${cardImgSizingRule} ${cardImgAspectRatio}`;

        // Create srcset for alternate image
        const alternateSrcset = [
          `${alternateImageUrl}&width=90 90w`,
          `${alternateImageUrl}&width=180 180w`,
          `${alternateImageUrl}&width=280 280w`,
          `${alternateImageUrl}&width=390 390w`,
          `${alternateImageUrl}&width=540 540w`,
          `${alternateImageUrl}&width=768 768w`,
          `${alternateImageUrl}&width=1080 1080w`,
          `${alternateImageUrl}&width=1280 1280w`,
          `${alternateImageUrl}&width=1500 1500w`
        ].join(', ');

        alternateFigureElement.innerHTML = `
              <img
                srcset="${alternateSrcset}"
                sizes="100vw"
                src="${alternateImageUrl}"
                alt="${productName}"
                loading="lazy"
                fetchpriority="low"
                decoding="async"
                onload="this.classList.add('loaded')"
              >
            `;

        // Add alternate image to hover wrapper
        hoverImagesDiv.appendChild(alternateFigureElement);

        primaryImageContainer.insertBefore(hoverImagesDiv, primaryImageContainer.firstChild);
      } else {
        // No hover functionality - add primary image directly
        const figureElement = document.createElement('figure');
        figureElement.className = `media ${cardImgSizingRule} ${cardImgAspectRatio}`;

        // Create srcset with multiple resolutions
        const srcset = [
          `${thumbUrl}&width=90 90w`,
          `${thumbUrl}&width=180 180w`,
          `${thumbUrl}&width=280 280w`,
          `${thumbUrl}&width=390 390w`,
          `${thumbUrl}&width=540 540w`,
          `${thumbUrl}&width=768 768w`,
          `${thumbUrl}&width=1080 1080w`,
          `${thumbUrl}&width=1280 1280w`,
          `${thumbUrl}&width=1500 1500w`
        ].join(', ');

        figureElement.innerHTML = `
              <img
                srcset="${srcset}"
                sizes="100vw"
                src="${thumbUrl}"
                alt="${productName}"
                loading="${loadingStrategy}"
                fetchpriority="${fetchPriority}"
                decoding="async"
                onload="this.classList.add('loaded')"
              >
            `;

        primaryImageContainer.insertBefore(figureElement, primaryImageContainer.firstChild);
      }
    }
  }

  /**
   * Helper method to set product badges
   */
  _setProductBadges(container, customFields) {
    try {
      let customBadges;
      if (Array.isArray(customFields)) {
        customBadges = customFields.find(f => f.key === 'woolman-custom_badges')?.value;
      } else if (typeof customFields === 'object' && customFields !== null) {
        customBadges = customFields['woolman-custom_badges'];
      }

      if (customBadges && customBadges.length > 0) {
        customBadges = typeof customBadges === 'string' ? JSON.parse(customBadges) : customBadges;
        const badgesElement = container.querySelector('[data-product-badges]');
        if (badgesElement && window.badges) {
          const badgeMarkup = customBadges.map(badgeName => {
            const badge = window.badges[badgeName];
            if (!badge) return '';
            return `
                  <span
                    class="badge ${badgeName.toLowerCase()}"
                    style="
                      --text-color: ${badge.text_color};
                      --background-color: ${badge.background_color};
                    "
                  >
                    ${badge.label}
                  </span>
                `;
          }).join('');
          badgesElement.innerHTML = badgeMarkup;
        }
      }
    } catch (error) {
      console.error('Error setting product badges:', error);
    }
  }

  /**
   * Helper method to set product price
   */
  _setProductPrice(container, price, listPrice, onDiscount, productBrand, product) {
    const priceElement = container.querySelector('[data-price]');
    let salePriceHtml = '';
    let shouldUseBlackPricing = false;

    // Check if this vendor should use black pen pricing
    if (productBrand && window.blackPenVendors?.length > 0 && window.blackPenVendors.find((item) => item.vendor.toLowerCase() == productBrand.toLowerCase())) {
      shouldUseBlackPricing = true;
    }

    // Add black pen pricing class to the card if applicable
    const cardElement = container.querySelector('.card--product');
    if (cardElement && shouldUseBlackPricing && onDiscount) {
      cardElement.classList.add('black-pen-pricing');
    }

    // Check if any SKUs/variants are on sale or have lower/higher prices
    let hasVariantOnSale = false;
    let minSkuPrice = null;
    let maxSkuPrice = null;
    let showPriceRange = false;

    if (product && product.skus && Array.isArray(product.skus) && product.skus.length > 1) {
      for (const sku of product.skus) {
        const skuPrice = sku.price;
        const skuListPrice = sku.listPrice;

        // Track the minimum and maximum SKU prices
        if (skuPrice && typeof skuPrice === 'number') {
          if (minSkuPrice === null || skuPrice < minSkuPrice) {
            minSkuPrice = skuPrice;
          }
          if (maxSkuPrice === null || skuPrice > maxSkuPrice) {
            maxSkuPrice = skuPrice;
          }
        }

        // Check if this SKU is on sale (has both prices and sale price is lower)
        if (skuListPrice && skuPrice && typeof skuPrice === 'number' && typeof skuListPrice === 'number' && skuPrice < skuListPrice) {
          hasVariantOnSale = true;
        }
      }

      // Show price range if we have multiple variants with different prices
      if (minSkuPrice !== null && maxSkuPrice !== null && minSkuPrice !== maxSkuPrice) {
        showPriceRange = true;

        // If any variant is on sale, mark the entire range as on sale
        if (hasVariantOnSale) {
          onDiscount = true;
        }
      }
    }

    // Set up price display
    let priceDisplay;
    if (showPriceRange) {
      const minPriceFormatted = Woolman.Utils.formatMoney(minSkuPrice * 100);
      const maxPriceFormatted = Woolman.Utils.formatMoney(maxSkuPrice * 100);
      priceDisplay = `${minPriceFormatted} - ${maxPriceFormatted}`;
    } else {
      priceDisplay = price;
    }

    if (onDiscount) {
      priceElement.classList.add('price--on-sale');
      if (shouldUseBlackPricing) {
        priceElement.classList.add('price--black-pen');
      }
      salePriceHtml = `
    <span class="visually-hidden">Sale price</span>
    <span class="price-item price-item--sale price-item--last">${priceDisplay}</span>
    `;
    }

    if (priceElement) {
      priceElement.innerHTML = `
          <div class="flex">
            <div class="price__regular">
              <span class="visually-hidden">Regular price</span>
              <span class="price-item price-item--regular">${priceDisplay}</span>
            </div>
            ${listPrice && !showPriceRange ? `
              <div class="price__sale">
                <span class="visually-hidden">Regular price</span>
                <span>
                  <s class="price-item price-item--regular style--subdued">${listPrice}</s>
                  ${salePriceHtml}
                </span>

              </div>
            ` : showPriceRange && onDiscount ? `
              <div class="price__sale">
                <span class="visually-hidden">Sale price</span>
                <span class="price-item--sale">
                  ${salePriceHtml}
                </span>
              </div>
            ` : ''}
          </div>
        `;
    }
  }

  _generateContentSlotElement(contentSlot) {
    const contentSlotUrl = contentSlot.link;
    const contentSlotImageAlt = contentSlot.imageAltText || '';
    const contentSlotImageUrl = contentSlot.image || '';
    const contentSlotPosition = contentSlot.position;

    const contentCardTemplate = document.querySelector('.js-card-content-template');
    const contentCardTemplateContent = contentCardTemplate.content.cloneNode(true);
    const contentCardContainer = document.createElement('div');
    contentCardContainer.appendChild(contentCardTemplateContent);
    if (contentSlotUrl)
      contentCardContainer.innerHTML = contentCardContainer.innerHTML
        .replace(/\[\[product\.url\]\]/g, contentSlotUrl)
    const contentAriaLabel = window.accessibilityStrings.viewSomething.replace('{{ something }}', contentSlotImageAlt);
    contentCardContainer.querySelector('.card-media').setAttribute('aria-label', contentAriaLabel);
    this._setProductImage(contentCardContainer, contentSlotImageUrl, contentSlotImageAlt, contentSlotPosition, null);
    return contentCardContainer.querySelector('.card--content-slot');
  }

  /**
   * Helper method to handle slider arrows visibility
   */
  _handleArrowsVisibility(products) {
    try {
      if (this.gridySlider && this.iprDesktop && this.arrows) {
        if (products.length <= this.iprDesktop) {
          this.arrows.style.display = 'none';
        } else {
          // Ensure arrows are shown if there are more products than iprDesktop
          this.arrows.style.display = '';
        }
      }
    } catch (error) {
      console.error('Error handling arrows visibility:', error);
    }
  }

  /**
   * Helper method to set product swatches from sibling products or variants
   * @param {HTMLElement} container - The container element (can be temp container or actual card)
   * @param {Object} product - The product data
   * @param {Object} customFields - Custom fields from the product
   * @param {HTMLElement} targetSwatchContainer - Optional specific swatch container to update
   */
  async _setProductSwatches(container, product, customFields, targetSwatchContainer = null) {
    try {
      const swatchesContainer = targetSwatchContainer || container.querySelector('[data-product-swatches]');
      if (!swatchesContainer) return;

      // First, check for sibling products GID from custom fields
      let siblingProductsGid = null;
      if (Array.isArray(customFields)) {
        const siblingField = customFields.find(f => f.key === 'woolman-sibling_linking');
        siblingProductsGid = siblingField?.value;
      } else if (typeof customFields === 'object' && customFields !== null) {
        siblingProductsGid = customFields['woolman-sibling_linking'];
      }

      // If sibling products exist, use them
      if (siblingProductsGid) {
        const siblingProducts = await this._fetchSiblingProducts(siblingProductsGid);
        if (siblingProducts && siblingProducts.length > 1) {
          const swatchesMarkup = await this._generateSwatchesMarkup(siblingProducts, product, 'sibling');
          swatchesContainer.innerHTML = swatchesMarkup;
          // Attach hover listeners after rendering
          this._attachSwatchHoverListeners(swatchesContainer, container);
          return;
        }
      }

      // Otherwise, check for color variants within the same product
      const productHandle = product.url?.split('/').slice(-1)[0] || '';
      if (productHandle) {
        const variants = await this._fetchProductVariants(productHandle);
        if (variants && variants.length > 1) {
          const swatchesMarkup = await this._generateSwatchesMarkup(variants, product, 'variant');
          swatchesContainer.innerHTML = swatchesMarkup;
          // Attach hover listeners after rendering
          this._attachSwatchHoverListeners(swatchesContainer, container);
        }
      }
    } catch (error) {
      console.error('Error setting product swatches:', error);
    }
  }

  /**
   * Fetch sibling products from Shopify metaobject
   */
  async _fetchSiblingProducts(gid) {
    try {
      if (!window.storefrontAccessToken) {
        console.warn('Storefront access token not configured - cannot fetch sibling products');
        return [];
      }

      // First, get the metaobject to retrieve the product GIDs
      const metaobjectQuery = `
        query getSiblingProductGids($id: ID!) {
          metaobject(id: $id) {
            fields {
              key
              value
            }
          }
        }
      `;

      const metaobjectResponse = await fetch('/api/2025-07/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.storefrontAccessToken
        },
        body: JSON.stringify({
          query: metaobjectQuery,
          variables: { id: gid }
        })
      });

      const metaobjectData = await metaobjectResponse.json();

      if (!metaobjectResponse.ok) {
        throw new Error(`HTTP error! status: ${metaobjectResponse.status}`);
      }

      if (metaobjectData.errors) {
        console.error('GraphQL errors:', metaobjectData.errors);
        return [];
      }

      // Find the linked_products field and extract the product GIDs
      const linkedProductsField = metaobjectData.data?.metaobject?.fields?.find(f =>
        f.key === 'linked_products' || f.key === 'products'
      );

      if (!linkedProductsField || !linkedProductsField.value) {
        console.warn('No linked_products field found in metaobject');
        return [];
      }

      // Parse the product GIDs (they should be in the value as a JSON array or string)
      let productGids;
      try {
        productGids = typeof linkedProductsField.value === 'string'
          ? JSON.parse(linkedProductsField.value)
          : linkedProductsField.value;
      } catch (e) {
        console.error('Error parsing linked_products value:', e);
        return [];
      }

      if (!Array.isArray(productGids) || productGids.length === 0) {
        console.warn('No product GIDs found in linked_products field');
        return [];
      }

      // Now fetch the actual products using their GIDs
      const productsQuery = `
        query getSiblingProducts($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on Product {
              id
              handle
              title
              availableForSale
              featuredImage {
                url
                altText
              }
              options {
                name
                values
              }
              variants(first: 1) {
                nodes {
                  id
                  barcode
                  metafield_colour_swatch: metafield(namespace: "creative_force", key: "colour_swatch") {
                    value
                  }
                  metafield_item_number: metafield(namespace: "d365", key: "item_number") {
                    value
                  }
                  metafield_fenwick_colour: metafield(namespace: "d365", key: "fenwick_colour") {
                    value
                  }
                }
              }
            }
          }
        }
      `;

      const productsResponse = await fetch('/api/2025-07/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.storefrontAccessToken
        },
        body: JSON.stringify({
          query: productsQuery,
          variables: { ids: productGids }
        })
      });

      const productsData = await productsResponse.json();

      if (!productsResponse.ok) {
        throw new Error(`HTTP error! status: ${productsResponse.status}`);
      }

      if (productsData.errors) {
        console.error('GraphQL errors:', productsData.errors);
        return [];
      }

      const products = productsData.data?.nodes?.filter(node => node !== null) || [];

      // Collect all MediaImage GIDs from colour_swatch metafields
      const mediaImageGids = [];
      products.forEach(product => {
        const variant = product.variants?.nodes?.[0];
        const swatchImageGid = variant?.metafield_colour_swatch?.value;
        if (swatchImageGid && swatchImageGid.startsWith('gid://shopify/MediaImage/')) {
          mediaImageGids.push(swatchImageGid);
        }
      });

      // Fetch the actual image URLs if we have any MediaImage GIDs
      let mediaImages = {};
      if (mediaImageGids.length > 0) {
        const mediaQuery = `
          query getMediaImages($ids: [ID!]!) {
            nodes(ids: $ids) {
              ... on MediaImage {
                id
                image {
                  url
                  altText
                }
              }
            }
          }
        `;

        const mediaResponse = await fetch('/api/2025-07/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': window.storefrontAccessToken
          },
          body: JSON.stringify({
            query: mediaQuery,
            variables: { ids: mediaImageGids }
          })
        });

        const mediaData = await mediaResponse.json();

        if (!mediaData.errors) {
          // Create a mapping of MediaImage GID to image URL
          mediaData.data?.nodes?.forEach(node => {
            if (node && node.image) {
              mediaImages[node.id] = node.image.url;
            }
          });
        }
      }

      // Add the actual image URLs to the products
      products.forEach(product => {
        const variant = product.variants?.nodes?.[0];
        if (variant?.metafield_colour_swatch?.value) {
          const mediaImageGid = variant.metafield_colour_swatch.value;
          if (mediaImages[mediaImageGid]) {
            variant.metafield_colour_swatch.imageUrl = mediaImages[mediaImageGid];
          }
        }
      });

      return products;
    } catch (error) {
      // Silently handle aborted requests (user navigated away)
      if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
        return [];
      }
      // Log other errors
      console.error('Error fetching sibling products:', error);
      return [];
    }
  }

  /**
   * Fetch product variants with color option from Shopify Storefront API
   */
  async _fetchProductVariants(productHandle) {
    try {
      if (!window.storefrontAccessToken) {
        console.warn('Storefront access token not configured - cannot fetch product variants');
        return [];
      }

      // Query to get all variants of a product with color option
      const variantsQuery = `
        query getProductVariants($handle: String!) {
          product(handle: $handle) {
            id
            handle
            title
            availableForSale
            options {
              name
              values
            }
            variants(first: 100) {
              nodes {
                id
                title
                availableForSale
                barcode
                image {
                  url
                  altText
                }
                selectedOptions {
                  name
                  value
                }
                metafield_colour_swatch: metafield(namespace: "creative_force", key: "colour_swatch") {
                  value
                }
                metafield_item_number: metafield(namespace: "d365", key: "item_number") {
                  value
                }
                metafield_fenwick_colour: metafield(namespace: "d365", key: "fenwick_colour") {
                  value
                }
              }
            }
          }
        }
      `;

      const variantsResponse = await fetch('/api/2025-07/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.storefrontAccessToken
        },
        body: JSON.stringify({
          query: variantsQuery,
          variables: { handle: productHandle }
        })
      });

      const variantsData = await variantsResponse.json();

      if (!variantsResponse.ok) {
        throw new Error(`HTTP error! status: ${variantsResponse.status}`);
      }

      if (variantsData.errors) {
        console.error('GraphQL errors:', variantsData.errors);
        return [];
      }

      const product = variantsData.data?.product;
      if (!product) {
        console.warn('Product not found');
        return [];
      }

      // Find the color option (could be named Colour, Color, etc.)
      const colorOption = product.options?.find(opt =>
        opt.name.toLowerCase().includes('colour') || opt.name.toLowerCase().includes('color')
      );

      if (!colorOption) {
        // No color option, return empty array
        return [];
      }

      const variants = product.variants?.nodes || [];

      // Filter variants to only include unique color values
      const seenColors = new Set();
      const uniqueColorVariants = variants.filter(variant => {
        const colorValue = variant.selectedOptions?.find(opt =>
          opt.name.toLowerCase().includes('colour') || opt.name.toLowerCase().includes('color')
        )?.value;

        if (!colorValue || seenColors.has(colorValue.toLowerCase())) {
          return false;
        }
        seenColors.add(colorValue.toLowerCase());
        return true;
      });

      // Collect all MediaImage GIDs from colour_swatch metafields
      const mediaImageGids = [];
      uniqueColorVariants.forEach(variant => {
        const swatchImageGid = variant?.metafield_colour_swatch?.value;
        if (swatchImageGid && swatchImageGid.startsWith('gid://shopify/MediaImage/')) {
          mediaImageGids.push(swatchImageGid);
        }
      });

      // Fetch the actual image URLs if we have any MediaImage GIDs
      let mediaImages = {};
      if (mediaImageGids.length > 0) {
        const mediaQuery = `
          query getMediaImages($ids: [ID!]!) {
            nodes(ids: $ids) {
              ... on MediaImage {
                id
                image {
                  url
                  altText
                }
              }
            }
          }
        `;

        const mediaResponse = await fetch('/api/2025-07/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': window.storefrontAccessToken
          },
          body: JSON.stringify({
            query: mediaQuery,
            variables: { ids: mediaImageGids }
          })
        });

        const mediaData = await mediaResponse.json();

        if (!mediaData.errors) {
          // Create a mapping of MediaImage GID to image URL
          mediaData.data?.nodes?.forEach(node => {
            if (node && node.image) {
              mediaImages[node.id] = node.image.url;
            }
          });
        }
      }

      // Add the actual image URLs to the variants and transform to match sibling product structure
      const transformedVariants = uniqueColorVariants.map(variant => {
        if (variant?.metafield_colour_swatch?.value) {
          const mediaImageGid = variant.metafield_colour_swatch.value;
          if (mediaImages[mediaImageGid]) {
            variant.metafield_colour_swatch.imageUrl = mediaImages[mediaImageGid];
          }
        }

        // Transform variant to match sibling product structure
        return {
          id: variant.id,
          handle: product.handle,
          title: variant.title,
          availableForSale: variant.availableForSale,
          isVariant: true, // Flag to distinguish from sibling products
          variantId: variant.id.split('/').pop(), // Extract numeric ID
          selectedOptions: variant.selectedOptions,
          metafield_colour_swatch: variant.metafield_colour_swatch,
          metafield_item_number: variant.metafield_item_number,
          metafield_fenwick_colour: variant.metafield_fenwick_colour,
          barcode: variant.barcode,
          image: variant.image // Include variant image
        };
      });

      return transformedVariants;
    } catch (error) {
      // Silently handle aborted requests (user navigated away)
      if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
        return [];
      }
      // Log other errors
      console.error('Error fetching product variants:', error);
      return [];
    }
  }

  /**
   * Generate swatches markup for sibling products or product variants
   * @param {Array} items - Array of sibling products or variants
   * @param {Object} currentProduct - Current Nosto product object
   * @param {String} type - 'sibling' or 'variant'
   */
  async _generateSwatchesMarkup(items, currentProduct, type = 'sibling') {
    // Render different swatch counts for mobile and desktop, let CSS handle visibility
    const MAX_SWATCHES_MOBILE = 4;
    const MAX_SWATCHES_DESKTOP = 7;

    const swatches = items.map(item => {
      let colorValue, isCurrentItem, isAvailable, swatchImage, ean, itemNumber, fenwickColour;

      if (type === 'variant') {
        // Handle regular product variants
        colorValue = item.selectedOptions?.find(opt =>
          opt.name.toLowerCase().includes('colour') || opt.name.toLowerCase().includes('color')
        )?.value;

        if (!colorValue) return null;

        // For variants, check if this is the current variant by comparing handles
        // Since Nosto doesn't give us variant ID, we compare the product URL
        const currentHandle = currentProduct.url?.split('/').slice(-1)[0] || currentProduct.handle;
        isCurrentItem = item.handle === currentHandle;
        isAvailable = item.availableForSale;

        // Skip sold-out variants
        if (!isAvailable) return null;

        // Get swatch data from variant metafields
        swatchImage = item.metafield_colour_swatch?.imageUrl || null;
        ean = item.barcode;
        itemNumber = item.metafield_item_number?.value;
        fenwickColour = item.metafield_fenwick_colour?.value;

        // Generate swatch HTML for variant
        return this._generateSingleSwatchMarkup({
          item,
          colorValue,
          swatchImage,
          ean,
          itemNumber,
          fenwickColour,
          isCurrentItem,
          isAvailable,
          type: 'variant'
        });
      } else {
        // Handle sibling products (original logic)
        const colorOption = item.options?.find(opt =>
          opt.name.toLowerCase().includes('colour') || opt.name.toLowerCase().includes('color')
        );

        if (!colorOption || !colorOption.values.length) return null;

        colorValue = colorOption.values[0];
        const variant = item.variants?.nodes?.[0];
        isCurrentItem = item.handle === currentProduct.handle;
        isAvailable = item.availableForSale;

        // Skip sold-out products
        if (!isAvailable) return null;

        // Get swatch data from variant metafields
        swatchImage = variant?.metafield_colour_swatch?.imageUrl || null;
        ean = variant?.barcode;
        itemNumber = variant?.metafield_item_number?.value;
        fenwickColour = variant?.metafield_fenwick_colour?.value;

        // Generate swatch HTML for sibling product
        return this._generateSingleSwatchMarkup({
          item,
          colorValue,
          swatchImage,
          ean,
          itemNumber,
          fenwickColour,
          isCurrentItem,
          isAvailable,
          type: 'sibling'
        });
      }
    }).filter(Boolean);

    if (swatches.length > 1) {
      const totalSwatches = swatches.length;

      // Get product URL for the +X links
      const productUrl = currentProduct.url?.split('/').slice(-1)[0]
        ? `/products/${currentProduct.url.split('/').slice(-1)[0]}`
        : '#';

      // Generate mobile swatches (first 4)
      const mobileSwatches = swatches.slice(0, MAX_SWATCHES_MOBILE);
      const mobileRemainingCount = totalSwatches - MAX_SWATCHES_MOBILE;
      const mobileMoreLink = mobileRemainingCount > 0
        ? `<a href="${productUrl}" class="swatches-more link">+${mobileRemainingCount}</a>`
        : '';

      // Generate desktop swatches (first 7)
      const desktopSwatches = swatches.slice(0, MAX_SWATCHES_DESKTOP);
      const desktopRemainingCount = totalSwatches - MAX_SWATCHES_DESKTOP;
      const desktopMoreLink = desktopRemainingCount > 0
        ? `<a href="${productUrl}" class="swatches-more link">+${desktopRemainingCount}</a>`
        : '';

      return `
        <div class="mt-12 nosto-swatches-container fade-in">
          <span class="visually-hidden">Available in</span>
          <div class="swatches swatches--mobile flex" style="gap: 0.6rem;">
            ${mobileSwatches.join('')}
            ${mobileMoreLink}
          </div>
          <div class="swatches swatches--desktop flex" style="gap: 0.6rem;">
            ${desktopSwatches.join('')}
            ${desktopMoreLink}
          </div>
        </div>
      `;
    }

    return '';
  }

  /**
   * Generate single swatch markup
   * @param {Object} params - Parameters object
   * @param {Object} params.item - Product or variant object
   * @param {String} params.colorValue - Color value to display
   * @param {String} params.swatchImage - Swatch image URL
   * @param {String} params.ean - EAN barcode
   * @param {String} params.itemNumber - Item number
   * @param {String} params.fenwickColour - Fenwick colour code
   * @param {Boolean} params.isCurrentItem - Whether this is the current item
   * @param {Boolean} params.isAvailable - Whether the item is available
   * @param {String} params.type - 'sibling' or 'variant'
   */
  _generateSingleSwatchMarkup({ item, colorValue, swatchImage, ean, itemNumber, fenwickColour, isCurrentItem, isAvailable, type = 'sibling' }) {
    let productUrl;

    if (type === 'variant') {
      // For variants, link to the same product with variant query parameter
      productUrl = `/products/${item.handle}?variant=${item.variantId}`;
    } else {
      // For sibling products, link to the different product
      productUrl = `/products/${item.handle}`;
    }

    const linkClass = `swatch ${isCurrentItem ? 'current-product' : ''} ${!isAvailable ? 'sold-out' : ''}`;

    // Get the image data for hover functionality
    let imageUrl = '';
    let imageAlt = '';

    if (type === 'variant') {
      // For variants, use the variant image
      imageUrl = item.image?.url || '';
      imageAlt = item.image?.altText || colorValue;
    } else {
      // For sibling products, use the featured image
      imageUrl = item.featuredImage?.url || '';
      imageAlt = item.featuredImage?.altText || item.title || colorValue;
    }

    // Determine swatch display method (following your existing logic)
    let swatchContent = '';

    if (swatchImage) {
      swatchContent = `
        <img
          src="${swatchImage}?width=110&height=110"
          alt="${colorValue}"
          title="${colorValue}"
          class="color"
          width="34"
          height="34"
          loading="lazy"
          fetchpriority="low"
          data-metafield-swatch
        >
      `;
    } else {
      // No swatch image available - skip this swatch
      return null;
    }

    // Add data attributes for hover image functionality
    const imageDataAttrs = imageUrl ? `data-hover-image="${imageUrl}" data-hover-image-alt="${imageAlt}"` : '';

    const linkElement = isAvailable ?
      `<a href="${productUrl}" class="${linkClass}" ${imageDataAttrs} tabindex="-1">` :
      `<div class="${linkClass}" ${imageDataAttrs}>`;

    const closingElement = isAvailable ? '</a>' : '</div>';

    return `
      ${linkElement}
        ${swatchContent}
        <span class="visually-hidden">${colorValue}</span>
      ${closingElement}
    `;
  }

  /**
   * Attach hover event listeners to swatches for image preview
   * @param {HTMLElement} swatchesContainer - The container with the swatches
   * @param {HTMLElement} productCard - The product card containing the image
   */
  _attachSwatchHoverListeners(swatchesContainer, productCard) {
    const swatches = swatchesContainer.querySelectorAll('.swatch[data-hover-image]');
    if (!swatches.length) return;

    // Find the primary image element in the product card
    const cardMedia = productCard.querySelector('.card-media');
    if (!cardMedia) return;

    // Check if there's a hover images wrapper, otherwise target the first image directly
    const hoverImagesWrapper = cardMedia.querySelector('.card-hover-images');
    const primaryImage = hoverImagesWrapper
      ? hoverImagesWrapper.querySelector('img')
      : cardMedia.querySelector('img');


    if (!primaryImage) return;

    // Store the original image data
    const originalImageSrc = primaryImage.getAttribute('src');
    const originalImageSrcset = primaryImage.getAttribute('srcset');
    const originalImageAlt = primaryImage.getAttribute('alt');

    // Track if we're currently hovering over any swatch
    let isHoveringSwatchArea = false;
    let currentHoveredSwatch = null;

    // Add mouseenter listener to each swatch to update the image
    swatches.forEach(swatch => {
      const hoverImageUrl = swatch.getAttribute('data-hover-image');
      const hoverImageAlt = swatch.getAttribute('data-hover-image-alt');

      if (!hoverImageUrl) return;

      swatch.addEventListener('mouseenter', () => {
        isHoveringSwatchArea = true;
        currentHoveredSwatch = swatch;

        // Update the image
        const imageUrl = hoverImageUrl.includes('?') ? hoverImageUrl : `${hoverImageUrl}?width=540`;

        // Update srcset if it exists
        if (originalImageSrcset) {
          const srcset = [
            `${hoverImageUrl}&width=90 90w`,
            `${hoverImageUrl}&width=180 180w`,
            `${hoverImageUrl}&width=280 280w`,
            `${hoverImageUrl}&width=390 390w`,
            `${hoverImageUrl}&width=540 540w`,
            `${hoverImageUrl}&width=768 768w`,
            `${hoverImageUrl}&width=1080 1080w`,
            `${hoverImageUrl}&width=1280 1280w`,
            `${hoverImageUrl}&width=1500 1500w`
          ].join(', ');
          primaryImage.setAttribute('srcset', srcset);
        }

        primaryImage.setAttribute('src', imageUrl);
        primaryImage.setAttribute('alt', hoverImageAlt || '');

        // Mark the hover images wrapper if it exists
        if (hoverImagesWrapper) {
          hoverImagesWrapper.classList.add('swatch-hover');
        }
      });
    });

    // Add mouseleave listener to the parent swatches container
    swatchesContainer.addEventListener('mouseleave', () => {
      isHoveringSwatchArea = false;
      currentHoveredSwatch = null;

      // Restore the original image
      if (originalImageSrc) {
        if (originalImageSrcset) {
          primaryImage.setAttribute('srcset', originalImageSrcset);
        }

        primaryImage.setAttribute('src', originalImageSrc);
        primaryImage.setAttribute('alt', originalImageAlt || '');
      }

      // Remove the swatch-hover class
      if (hoverImagesWrapper) {
        hoverImagesWrapper.classList.remove('swatch-hover');
      }
    });
  }

  /**
   * Find swatch image URL by EAN or item number
   */
  _findSwatchImageUrl(ean, itemNumber) {
    // Check if images exist in the global images object (if available)
    if (window.images) {
      let url = ean ? `${ean}_swatch.png` : null;
      if (url && window.images[url]) {
        return window.images[url];
      }

      url = ean ? `${ean}_swatch.jpg` : null;
      if (url && window.images[url]) {
        return window.images[url];
      }

      url = itemNumber ? `${itemNumber}_swatch.png` : null;
      if (url && window.images[url]) {
        return window.images[url];
      }

      url = itemNumber ? `${itemNumber}_swatch.jpg` : null;
      if (url && window.images[url]) {
        return window.images[url];
      }
    }

    return null;
  }

  /**
   * Set loading state for this placement
   */
  setLoadingState() {
    if (this.loadingSpinner) {
      this.loadingSpinner.classList.remove('hidden');
    }
    if (this.container) {
      this.container.setAttribute('loading', '');
    }
    if (this.productGrid) {
      this.productGrid.setAttribute('loading', '');
    }
  }

  /**
   * Remove loading state for this placement
   */
  removeLoadingState() {
    if (this.loadingSpinner) {
      this.loadingSpinner.classList.add('hidden');
    }
    if (this.container) {
      this.container.removeAttribute('loading');
    }
    if (this.productGrid) {
      this.productGrid.removeAttribute('loading');
    }
  }

  /**
   * Hide the wrapper element for this placement
   */
  hideWrapper() {
    if (this.container) {
      this.container.classList.add('hidden');
    }
    if (this.productGrid) {
      this.productGrid.classList.add('hidden');
    }
  }

  /**
   * Set up click tracking for product cards using event delegation
   */
  setupClickTracking() {
    if (this.productGrid) {
      document.addEventListener('quickBuy:modalOpened', (event) => {
        const productId = event.detail.productId;
        const productUrl = event.detail.productUrl;
        if (productUrl && productId) {
          this.trackSearchClick(productId, productUrl);
        }
      });
      this.productGrid.addEventListener('click', this.handleProductClick.bind(this));
    }
  }

  /**
   * Handle click events on product cards and track via Nosto API
   * @param {Event} event - Click event
   */
  handleProductClick(event) {
    if (event.target.closest('quick-buy')) return;
    const card = event.target.classList.contains('card--product') ? event.target : event.target.closest('.card--product');
    if (!card) return;

    const productId = card.getAttribute('data-product-id') || card.getAttribute('data-track-product-id');
    const productUrl = `${window.location.origin}${card.querySelector('a')?.getAttribute('href')}`;

    if (productUrl && productId) {
      this.trackSearchClick(productId, productUrl);
    }
  }

  /**
   * Track when a product in search results is clicked
   * @param {string} productId - ID of the product that was clicked
   * @param {string} productUrl - URL of the product page
   */
  trackSearchClick(productId, productUrl) {
    if (!window.nostojs) return;
    nostojs(api => {
      api.recordSearchClick(
        this.context,
        {
          productId: productId,
          url: productUrl
        }
      );
    });
  }

  updatePaginationState() {
    const total = parseInt(this.productGrid?.getAttribute('data-filtered-count')) || 0;
    const totalPages = Math.ceil(total / this.resultsSize);

    // Update SEO pagination links
    const seoPagination = document.querySelector('.seo-pagination');
    if (seoPagination) {
      seoPagination.innerHTML = this.generatePaginationLinks(this.currentPage, totalPages);
    }

    // Update meta tags
    this.updatePaginationMeta(this.currentPage, totalPages);
  }

  generatePaginationLinks(currentPage, totalPages) {
    const baseUrl = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    params.delete('view')
    let links = '';

    if (currentPage > 1) {
      if (currentPage - 1 === 1) {
        params.delete('page');
      } else {
        params.set('page', currentPage - 1);
      }
      const paramsStr = params.toString() ? `?${params.toString()}` : '';
      links += `<a href="${baseUrl}${paramsStr}" rel="prev">Previous</a>`;
    }

    if (currentPage < totalPages) {
      params.set('page', currentPage + 1);
      const paramsStr = params.toString() ? `?${params.toString()}` : '';
      links += `<a href="${baseUrl}${paramsStr}" rel="next">Next</a>`;
    }

    return links;
  }

  updatePaginationMeta(currentPage, totalPages) {
    // Remove existing rel meta tags
    document.querySelectorAll('link[rel="prev"], link[rel="next"]').forEach(el => el.remove());

    const baseUrl = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    params.delete('view');

    // Add prev meta tag if not on first page
    if (currentPage > 1) {
      if (currentPage - 1 === 1) {
        params.delete('page');
      } else {
        params.set('page', currentPage - 1);
      }
      const paramsStr = params.toString() ? `?${params.toString()}` : '';
      const prevLink = document.createElement('link');
      prevLink.rel = 'prev';
      prevLink.href = `${baseUrl}${paramsStr}`;
      document.head.appendChild(prevLink);
    }

    // Add next meta tag if not on last page
    if (currentPage < totalPages) {
      params.set('page', currentPage + 1);
      const paramsStr = params.toString() ? `?${params.toString()}` : '';
      const nextLink = document.createElement('link');
      nextLink.rel = 'next';
      nextLink.href = `${baseUrl}${paramsStr}`;
      document.head.appendChild(nextLink);
    }
  }

  /**
   * Set up initial state from URL parameters
   */
  initializeFromURL() {
    const params = new URLSearchParams(window.location.search);
    this.currentPage = parseInt(params.get('page')) || 1;

    // Handle keyword parameter
    const isKeyword = params.get('keyword') === 'true';
    if (isKeyword) {
      params.set('keyword', 'true');
    }

    // Update URL and sort options
    this.updateQuery(params);
    if (params.has('sort_by') && this.sortBy) {
      this.sortBy.value = params.get('sort_by');
    }

    // Set loading state and fetch results
    this.setLoadingState();
    this.getSearchResults();
    this.updatePaginationState();
  }

  /**
   * Set up all event listeners for the search component
   */
  setupEventListeners() {
    // Store bound event handlers as class properties
    this.boundFacetChangeHandler = this.onFacetChange.bind(this);
    this.boundFacetClearHandler = this.onFacetClear.bind(this);

    // Use optional chaining to prevent errors if elements don't exist
    this.searchForm?.addEventListener('submit', this.onFormSubmit.bind(this));
    this.sortBy?.addEventListener('change', this.onSelectChange.bind(this));
    this.viewMoreButton?.addEventListener('click', this.onViewMoreClick.bind(this));
  }

  /**
   * Clean up event listeners when element is removed
   */
  disconnectedCallback() {
    // Clean up abort controller
    if (this.abortController) {
      this.abortController.abort();
    }

    // Remove event listeners from facet elements
    this.facetsFormContainer?.querySelectorAll('input').forEach((item) => {
      item.removeEventListener('change', this.boundFacetChangeHandler);
    });

    this.facetsFormContainer?.querySelectorAll('.facet-clear--single').forEach((item) => {
      item.removeEventListener('click', this.boundFacetClearHandler);
    });

    // Clean up event listeners from main elements
    this.searchForm?.removeEventListener('submit', this.onFormSubmit);
    this.sortBy?.removeEventListener('change', this.onSelectChange);
    this.viewMoreButton?.removeEventListener('click', this.onViewMoreClick);
  }

  onFormSubmit(event) {
    event.preventDefault();
    this.clearFilters();
    this.setLoadingState();
    this.getNostoSearchResults();
    this.getSearchResults();
  }

  onFormReset(event) {
    event.preventDefault();

    this.input.value = '';
    this.input.focus();
    this.toggleResetButton();
    this.searchTerm = '';
    this.abortController.abort();
    this.abortController = new AbortController();
    this.closeResults(true);
  }

  onSelectChange(event) {
    if (this.usingFallback) {
      // Let Shopify sorting handle it
      return;
    }
    const value = event.target.value;
    const params = new URLSearchParams(window.location.search);
    params.set('sort_by', value);
    this.updateQuery(params);
    this.setLoadingState();
    this.getNostoSearchResults();
    this.getSearchResults();
  }

  onViewMoreClick(event) {
    event.preventDefault();
    const params = new URLSearchParams(window.location.search);

    // Update the current page
    this.currentPage++;
    params.set('page', this.currentPage);

    // Remove legacy 'from' parameter if it exists
    params.delete('from');

    this.updateQuery(params);
    this.setLoadingState();
    this.getNostoSearchResults();
    this.getSearchResults();
  }

  updateQuery(params = null) {
    params = params ? params : new URLSearchParams(window.location.search);
    if (this.context == 'serp') {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;
      this.searchTerm = searchTerm;
      params.set('q', searchTerm);
    }
    const queryStr = params.toString();
    const url = !queryStr ? window.location.pathname : `${window.location.pathname}?${queryStr}`;
    window.history.replaceState({ path: url }, '', url);
  }

  clearFilters() {
    const params = new URLSearchParams(window.location.search);
    this.activeMaxPrice = null;
    for (const [param, value] of params.entries()) {
      if (param.startsWith('filter.')) {
        params.delete(param);
      }
    }
    // Reset to page 1 when clearing filters
    params.delete('page');
    this.currentPage = 1;
    this.updateQuery(params);
  }

  getQuery() {
    return this.input.value.trim();
  }

  /**
   * Get search results from cache, Nosto API or fallback to Shopify
   */
  async getSearchResults() {
    // Update header text for search context
    if (this.context === 'serp' && this.header) {
      this.header.textContent = `${window.nostoStrings?.searchResultsFor || 'Search results for'} "${this.searchTerm}"`;
    }
    const cacheKey = location.search || location.href;

    // Try to get cached results first for better performance
    if (!this.usingFallback && this.cachedResults[cacheKey]) {
      this.renderSearchResults(this.cachedResults[cacheKey]);
      return;
    }

    // Check if we should skip Nosto due to previous failures
    this.checkNostoAvailability();

    if (this.usingFallback && this.enableFallback) {
      return this.fallbackToShopify();
    }

    try {
      // Create a new abort controller for this request
      this.abortController = new AbortController();

      // Get Nosto search results
      // const nostoResults = await this.results;
      const nostoResults = await window.preloadedNostoResults;

      if (nostoResults?.products?.hits) {
        // Cache the results for faster subsequent requests
        this.cachedResults[cacheKey] = nostoResults;
        this.renderSearchResults(nostoResults);

        // Reset retry counter on success
        sessionStorage.removeItem('fallbackAttempts');
      } else {
        // Handle empty results
        this.renderNoResults();

        // Reset retry counter as this is a valid response (just empty)
        sessionStorage.removeItem('fallbackAttempts');
      }
    } catch (error) {
      console.error('Nosto search error:', error);

      // Get current attempt count from sessionStorage
      const currentAttempts = parseInt(sessionStorage.getItem('fallbackAttempts') || '0');
      const newAttempts = currentAttempts + 1;

      // Store updated attempt count
      sessionStorage.setItem('fallbackAttempts', String(newAttempts));

      console.log(`Nosto API attempt ${newAttempts} of ${this.maxRetries} failed`);

      if (newAttempts >= this.maxRetries) {
        console.log('Max attempts reached, setting skipNostoUntil and falling back to Shopify');

        if (this.enableFallback) {
          // Set skipNostoUntil in sessionStorage for 10 minutes
          sessionStorage.setItem('skipNostoUntil', String(Date.now() + 600000));
          return this.fallbackToShopify();
        } else {
          // Show error state if fallback is disabled
          this.removeLoadingState();
          this.renderErrorState();
        }
      } else {
        // Immediately fallback to Shopify without retrying Nosto
        if (this.enableFallback) {
          return this.fallbackToShopify();
        } else {
          // Show error state if fallback is disabled
          this.removeLoadingState();
          this.renderErrorState();
        }
      }
      return; // Exit early to avoid updating pagination state
    }

    // Update pagination state after results are loaded
    this.updatePaginationState();
  }

  /**
   * Check if Nosto should be skipped based on previous failures
   */
  checkNostoAvailability() {
    const skipNostoUntil = sessionStorage.getItem('skipNostoUntil');
    const currentTime = Date.now();

    if (skipNostoUntil && currentTime <= parseInt(skipNostoUntil)) {
      this.usingFallback = true;
      // Reset retry counter when using fallback
      this.retryCount = 0;
    } else {
      this.usingFallback = false;
      // Clear expired setting
      if (skipNostoUntil) {
        sessionStorage.removeItem('skipNostoUntil');
      }
    }
  }

  async fallbackToShopify() {
    this.usingFallback = true;

    try {
      // Instead of fetching HTML, redirect to the same URL with view=fallback parameter
      const currentParams = new URLSearchParams(window.location.search);

      // Add the view parameter to trigger the fallback template
      currentParams.set('view', 'fallback');

      // If we're on search page and have a search term, make sure it's in the params
      if (this.context === 'serp' && this.searchTerm && !currentParams.has('q')) {
        currentParams.set('q', this.searchTerm);
      }

      // Construct the fallback URL
      const fallbackUrl = `${window.location.pathname}?${currentParams.toString()}`;

      // Redirect to the fallback URL
      window.location.href = fallbackUrl;
    } catch (err) {
      console.error('Shopify fallback error:', err);
      // In case of an error with redirection, show an error message
      this.renderErrorState();
    }
  }

  async getNostoSearchResults() {
    const params = new URLSearchParams(window.location.search);
    const isKeyword = params.get('keyword') == 'true' ? true : false;
    const filters = [];
    const sortBy = [];

    if (params.has('sort_by')) {
      const sortValue = params.get('sort_by');
      // Skip relevance, since it's the default sort
      if (sortValue !== 'relevance') {
        let field = sortValue;
        let order = 'asc';
        if (sortValue.endsWith('-ascending')) {
          field = sortValue.replace('-ascending', '');
          order = 'asc';
        } else if (sortValue.endsWith('-descending')) {
          field = sortValue.replace('-descending', '');
          order = 'desc';
        }
        if (!field.includes('price')) {
          field = `stats.${field}`;
        }
        sortBy.push({
          field: field,
          order: order
        });
      }
    }

    // Calculate 'from' based on current page
    const currentPage = parseInt(params.get('page')) || 1;
    const from = (currentPage - 1) * this.resultsSize;

    // Iterate through all params to build filters array
    for (const [param, value] of params.entries()) {
      // Skip non-filter params
      if (!param.startsWith('filter.') || filters.some(f => f.field === 'price')) continue;
      if (param === 'filter.price') {
        const [range, max] = value.split('~');
        const [gte, lte] = range.split('-');
        filters.push({
          field: 'price',
          range: { lte: lte, gte: gte }
        });
      } else {
        // Remove filter. prefix to get param name
        const paramName = param.replace('filter.', '').toLowerCase();
        // Get the corresponding API field from window.filterToNosto
        const apiField = window.filterToNosto && window.filterToNosto[paramName] ? window.filterToNosto[paramName] : paramName;
        if (!apiField) {
          console.error(`Invalid filter parameter: ${paramName}`);
          continue;
        };
        // Split value string into array by pipe character
        const valueArray = value.split('|');
        filters.push({
          field: apiField,
          value: valueArray
        });
      }
    }
    // Parameters for the Nosto. Remember to keep these in sync with nosto-async.liquid (initial load of the page)
    try {
      const searchParams = {
        products: {
          facets: ['*'],
          fields: [
            'name',
            'imageUrl',
            'alternateImageUrls',
            'price',
            'url',
            'brand',
            'listPrice',
            'customFields.key',
            'customFields.value',
            'skus.id',
            'skus.price',
            'skus.listPrice'
          ],
          size: this.resultsSize,
          from: from // Use calculated 'from' value
        }
      };

      const trackParams = {
        redirect: true,
        track: this.context,
        isKeyword: isKeyword
      }

      if (this.context === 'serp') {
        searchParams.query = this.searchTerm;
      } else if (this.context === 'category') {
        if (location.pathname !== '/collections/all') {
          searchParams.products.categoryId = this.collectionId;
        }
      }

      if (filters.length > 0) {
        searchParams.products.filter = filters;
      }

      if (sortBy.length > 0) {
        searchParams.products.sort = sortBy;
      }

      window.preloadedNostoResults = new Promise(resolve => {
        nostojs(async api => {
          if (this.context === 'serp') {
            api.recordSearchSubmit(searchParams.query)
          }
          const results = api.search(searchParams, trackParams)

          if (!results || results.status) {
            throw new Error('Invalid Nosto API response');
          }
          resolve(results);
        });
      });

    } catch (error) {
      console.log(error)
      throw error;
    }
  }

  async renderSearchResults(results) {
    this.classList.remove('no-results')
    const products = results.products.hits;
    const resultsFrom = results.products.from;
    const resultsTotal = results.products.total;
    if (resultsTotal <= resultsFrom + this.resultsSize) {
      this.viewMoreButton.style.display = 'none';
    } else {
      this.viewMoreButton.style.display = 'block';
    }

    if (!products || products.length === 0) {
      this.renderNoResults();
      return;
    }
    this.processExcludeFacets(products[0]);
    if (this.context === 'serp') {
      document.title = `Search: ${resultsTotal} results found for "${this.searchTerm}"`;
    }
    this.resultsCount.textContent = `${resultsTotal} ${window.nostoStrings.searchResults}`;
    if (this.showCount) {
      const showFrom = resultsFrom + this.resultsSize;
      const showCount = showFrom > resultsTotal ? resultsTotal : showFrom;
      const countText = window.nostoStrings.searchShowCount || 'Showing {{ count }} of {{ total }} items';
      this.showCount.textContent = countText
        .replace('{{ count }}', showCount)
        .replace('{{ total }}', resultsTotal);
    }
    this.productGrid.setAttribute('data-filtered-count', results.products.total);
    this.renderFacets(results.products.facets);
    const productsMarkup = await this.generateProductsMarkup(products);

    if (resultsFrom > 0) {
      // For pagination/load more - append new content
      this.productGrid.innerHTML += productsMarkup;
    } else {
      // For initial load or filter change - replace content
      this.productGrid.innerHTML = productsMarkup;
    }

    // Check if content slots are available in options
    const contentSlots = this.options.contentSlots;
    if (contentSlots && Array.isArray(contentSlots) && contentSlots.length > 0) {
      // Sort content slots by position to insert them in order
      const sortedSlots = [...contentSlots].sort((a, b) => (a.position || 0) - (b.position || 0));

      // Insert each content slot at its specified position
      sortedSlots.forEach(contentSlot => {
        const contentSlotElem = this._generateContentSlotElement(contentSlot);
        const refNode = this.productGrid.querySelector(`.card--product[data-index="${contentSlot.position}"]`) ?? this.productGrid.lastChild;
        this.productGrid.insertBefore(contentSlotElem, refNode);
      });
    }

    // Add event listeners for image loading
    this.setupImageLoadingEvents();

    // Load swatches after products are rendered (non-blocking)
    this.loadDeferredSwatches();

    // Replace nr of results in document title
    this.removeLoadingState();
    // Dispatch custom event after Nosto products are rendered
    document.dispatchEvent(new CustomEvent('nosto:rendered', { detail: { placement: this } }));
  }

  processExcludeFacets(product) {
    const customFields = product.customFields;
    if (this.includeFacetRules && this.context === 'category') {
      let division;
      if (Array.isArray(customFields)) {
        division = customFields.find(f => f.key === 'd365-division')?.value?.toLowerCase();
      }
      if (division && !this.includeFacetRules[division]) {
        // Push all facets to excludeFacets where the division key doesn't match
        Object.keys(this.includeFacetRules).forEach(key => {
          if (key !== division) {
            this.excludeFacets.push(this.includeFacetRules[key]);
          }
        });
      }
    }
  }

  // New method to handle image loading events
  setupImageLoadingEvents() {
    const images = this.productGrid.querySelectorAll('img:not(.loaded)');
    images.forEach(img => {
      if (img.complete) {
        img.classList.add('loaded');
      } else {
        img.addEventListener('load', () => {
          img.classList.add('loaded');
        });
      }
    });
  }

  /**
   * Display an error state when both Nosto and fallback fail
   */
  renderErrorState() {
    if (!this.productGrid) return;

    this.productGrid.innerHTML = `
          <div class="mt-gutter mb-gutter">
            <p role="status">There was an error retrieving search results. Please try again later.</p>
            <a class="link" href="/">${window.nostoStrings?.continueShopping || 'Continue shopping'}</a>
          </div>
        `;
  }

  async renderFacets(facets) {
    if (!this.facetsFormContainer || !facets || !Array.isArray(facets)) {
      console.warn('Cannot render facets: Missing required elements or data');
      return;
    }

    // Store open facets state before clearing
    this.saveOpenFacetsState();

    // Get template references
    const templates = this.getFacetTemplates();
    if (!templates.facetsDetails) {
      console.error('Required facet templates not found');
      return;
    }

    // Clear existing facets
    this.facetsFormContainer.innerHTML = '';

    // Process each facet
    facets.forEach(facet => this.renderSingleFacet(facet, templates));

    // Add event listeners to new facet elements
    this.attachFacetEventListeners();

    // Scroll to last edited facet if applicable
    this.scrollToLastEditedFacet();
  }

  // Helper functions for facet rendering
  saveOpenFacetsState() {
    const openDetails = this.facetsFormContainer?.querySelectorAll('details[open]') || [];
    this.openedFacets = Array.from(openDetails).map(detail =>
      detail.getAttribute('data-filter-group')
    ).filter(Boolean);
  }

  getFacetTemplates() {
    return {
      facetsDetails: document.querySelector('#nosto-facets-details-template'),
      facetsListItem: document.querySelector('#nosto-facets-list-item-template'),
      facetsColor: document.querySelector('#nosto-facets-color-swatches-template'),
      facetsRange: document.querySelector('#nosto-facets-range-template')
    };
  }

  renderSingleFacet(facet, templates) {
    if (!facet || !facet.name) return;
    if (this.excludeFacets.includes(facet.name.toLowerCase())) return;

    const content = templates.facetsDetails.content.cloneNode(true);
    const tempContainer = document.createElement('div');
    tempContainer.appendChild(content);

    const tempContainerContentElem = tempContainer.querySelector('[data-facets-container]');
    const filterLabel = tempContainer.querySelector('summary span');
    filterLabel.textContent = facet.name;

    // Set up details element
    const detailsElem = tempContainer.querySelector('details');
    detailsElem.setAttribute('data-filter-group', facet.name);

    // Restore open state if this facet was open before
    if (this.openedFacets.includes(facet.name)) {
      detailsElem.setAttribute('open', '');
    }

    let facetActive = false;

    // Handle different facet types
    if (facet.type === 'terms') {
      facetActive = this.renderTermsFacet(facet, tempContainerContentElem, templates);
    } else if (facet.type === 'stats' && facet.name === "Price") {
      facetActive = this.renderPriceFacet(facet, tempContainerContentElem, templates);
    }

    // Toggle clear button visibility
    const clearButton = tempContainer.querySelector('.facet-clear');
    if (clearButton) {
      clearButton.style.display = facetActive ? 'block' : 'none';
    }

    // Add to the DOM
    this.facetsFormContainer.appendChild(tempContainer.firstElementChild);
  }

  renderTermsFacet(facet, containerElem, templates) {
    let facetActive = false;

    // Determine filter type
    let filterType = 'default';
    if (facet.name === 'Colour') {
      filterType = 'color';
    }

    // Create list container
    const listContainer = document.createElement('ul');
    listContainer.classList.add('filter-options-list', filterType);
    containerElem.appendChild(listContainer);

    // Add each facet value
    facet.data.forEach(item => {
      const content = templates.facetsListItem.content.cloneNode(true);
      const listItemContainer = document.createElement('li');
      listItemContainer.classList.add('filter-option');
      listItemContainer.appendChild(content);

      // Get parameter name, using mapping if available
      const facetNameLower = facet.name.toLowerCase().replace(' ', '-');
      const paramName = window.nostoToFilter && window.nostoToFilter[facet.field]
        ? window.nostoToFilter[facet.field]
        : facetNameLower;

      if (!window.nostoToFilter[facet.field]) {
        window.filterToNosto[facetNameLower] = facet.field;
      }

      // Replace placeholders in template
      listItemContainer.innerHTML = listItemContainer.innerHTML
        .replace(/\[\[param_name\]\]/g, paramName)
        .replace(/\[\[value\]\]/g, item.value)
        .replace(/\[\[count\]\]/g, item.count)
        .replace(/\[\[filter_type\]\]/g, filterType);

      const input = listItemContainer.querySelector('input');
      const inputClassType = filterType === 'color' ? 'for-swatch' : 'checkbox';
      input.classList.add(inputClassType);

      // Handle color swatches
      if (filterType === 'color') {
        this.addColorSwatch(listItemContainer, item.value, templates);
      }

      // Set checked state
      if (item.selected) {
        input.checked = true;
        facetActive = true;
      } else {
        input.removeAttribute('checked');
      }

      listContainer.appendChild(listItemContainer);
    });

    // Add search input for facets that should be searchable
    if (this.searchableFacets.includes(facet.field.toLowerCase())) {
      this.renderFacetSearch(listContainer, facet.field);
    }

    return facetActive;
  }

  addColorSwatch(container, colorValue, templates) {
    const content = templates.facetsColor.content.cloneNode(true);
    const swatchContainer = document.createElement('span');
    swatchContainer.classList.add('swatch');
    swatchContainer.appendChild(content);

    // Get color value, using mapping if available
    let facetColor = colorValue.toLowerCase();
    if (window.colorPatterns && window.colorPatterns[facetColor]) {
      facetColor = window.colorPatterns[facetColor];
    }

    swatchContainer.innerHTML = swatchContainer.innerHTML
      .replace(/\[\[color\]\]/g, colorValue)
      .replace(/\[\[color_hex\]\]/g, facetColor);

    const spanLabel = container.querySelector('span[data-option-label]');
    container.querySelector('label').insertBefore(swatchContainer, spanLabel);
  }

  renderPriceFacet(facet, containerElem, templates) {
    const content = templates.facetsRange.content.cloneNode(true);
    const priceRangeTempContainer = document.createElement('div');
    priceRangeTempContainer.appendChild(content);

    const params = new URLSearchParams(window.location.search);
    const priceParam = params.get('filter.price');
    let newMinValue = 0;
    let newMaxValue = facet.max;
    let maxPrice = facet.max;
    let facetActive = false;

    if (priceParam) {
      const [range, max] = priceParam.split('~');
      const [gte, lte] = range.split('-');
      newMinValue = parseInt(gte) || 0;
      newMaxValue = parseInt(lte) || facet.max;
      maxPrice = parseInt(max) || facet.max;
      facetActive = true;
    } else {
      this.activeMaxPrice = facet.max;
    }

    priceRangeTempContainer.innerHTML = priceRangeTempContainer.innerHTML
      .replace(/\[\[min_value\]\]/g, newMinValue)
      .replace(/\[\[max_value\]\]/g, newMaxValue)
      .replace(/\[\[gte_max\]\]/g, maxPrice - 1)
      .replace(/\[\[lte_max\]\]/g, maxPrice)
      .replace(/\[\[lte_min\]\]/g, newMinValue + 1);

    containerElem.appendChild(priceRangeTempContainer);

    return facetActive;
  }

  attachFacetEventListeners() {
    // Add change event listeners to inputs
    this.facetsFormContainer.querySelectorAll('input').forEach((item) => {
      item.addEventListener('change', debounce(this.boundFacetChangeHandler, 150));
    });

    // Add click event listeners to clear buttons
    this.facetsFormContainer.querySelectorAll('.facet-clear').forEach((item) => {
      item.addEventListener('click', debounce(this.boundFacetClearHandler, 150));
    });

    // Initialize accordions for details elements
    this.facetsFormContainer.querySelectorAll('details').forEach((el) => {
      new Accordion(el);
    });
  }

  scrollToLastEditedFacet() {
    if (!this.lastEditedFacet) return;

    const lastEditedFacetInput = this.facetsFormContainer.querySelector(
      `.filter-option input[data-key-value="${this.lastEditedFacet}"]`
    );

    if (lastEditedFacetInput) {
      // Use smooth scrolling and add slight delay to ensure the facet is open
      setTimeout(() => {
        lastEditedFacetInput.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  }

  renderFacetSearch(container, facetTitle) {
    const searchLabel = document.createElement('label');
    const inputTextContent = `${window.nostoStrings.search} ${facetTitle}s`;
    searchLabel.textContent = inputTextContent;
    searchLabel.classList.add('visually-hidden'); // Hides label visually but keeps it accessible

    const searchInput = document.createElement('input');
    searchInput.type = 'text';
    searchInput.placeholder = inputTextContent;
    searchInput.classList.add('facet-search-field');
    searchInput.setAttribute('aria-label', inputTextContent);

    const searchWrapper = document.createElement('div');
    searchWrapper.classList.add('facet-search');
    searchWrapper.appendChild(searchLabel);
    searchWrapper.appendChild(searchInput);

    container.parentNode.insertBefore(searchWrapper, container);

    searchInput.addEventListener('input', (e) => {
      const searchValue = e.target.value.toLowerCase();
      const listItems = container.querySelectorAll('li');
      listItems.forEach(item => {
        const label = item.querySelector('[data-option-label]').textContent.toLowerCase();
        item.style.display = label.includes(searchValue) ? '' : 'none';
      });
    });
  }

  async onFacetChange(event) {
    if (this.usingFallback) {
      // Let Shopify facets handle it
      return;
    }
    const target = event.target;
    if (target.classList.contains('facet-search-field')) return;
    const params = new URLSearchParams(window.location.search);
    const isPriceRange = target.classList.contains('price-range__input');
    const paramTitle = target.getAttribute('data-param-name').toLowerCase();

    // Reset pagination when changing filters
    params.delete('page');
    this.currentPage = 1;

    if (isPriceRange) {
      // Every price facet change triggers events for both min and max inputs, skip the min input and handle only the event from max input
      // If selected value is out of bounds, remove filter value from parameters
      const inputMin = target.getAttribute('name') === 'filter.price.gte';
      const inputMax = target.getAttribute('name') === 'filter.price.lte';
      if (inputMin && target.value < Math.floor(target.min)) {
        target.value = target.min;
        return;
      } else if (inputMax && target.value > Math.floor(target.max)) {
        target.value = target.max;
        return;
      } else {
        const minInput = this.facetsFormContainer.querySelector('input[name="filter.price.gte"]');
        const maxInput = this.facetsFormContainer.querySelector('input[name="filter.price.lte"]');

        // Skip if values are out of bounds
        if (parseFloat(minInput.value) < parseFloat(minInput.min) ||
          parseFloat(maxInput.value) > parseFloat(maxInput.max)) {
          return;
        }

        // Construct the new price parameter
        const priceValue = `${minInput.value}-${maxInput.value}`;
        if (this.activeMaxPrice) {
          params.set('filter.price', `${priceValue}~${this.activeMaxPrice}`);
        } else {
          params.set('filter.price', priceValue);
        }
      }
    }

    const keyValue = target.getAttribute('data-key-value');
    const param = `filter.${paramTitle}`;
    const value = target.getAttribute('data-value') || target.value;

    if (!param || !value) return;

    const existingValues = params.get(param)?.split('|') || [];
    if (!isPriceRange) {
      if (target.checked) {
        existingValues.push(value);
        params.set(param, existingValues.join('|'));
        this.lastEditedFacet = keyValue;
      } else {
        const newValues = existingValues.filter(v => v !== value);
        if (newValues.length === 0) {
          params.delete(param);
          this.lastEditedFacet = null;
        } else {
          params.set(param, newValues.join('|'));
        }
      }
    }

    this.updateQuery(params);
    // Skip event from min price input, price change is handled from max input
    if (target.getAttribute('data-min') !== null) return;
    this.setLoadingState();
    this.getNostoSearchResults();
    this.getSearchResults();
  }

  async onFacetClear(event) {
    event.preventDefault();
    const target = event.target;
    const params = new URLSearchParams(window.location.search);
    const parent = target.closest('details');
    const field = parent.getAttribute('data-filter-group')?.toLowerCase();

    // Reset pagination when clearing filters
    params.delete('page');
    this.currentPage = 1;

    if (field == 'price') {
      params.delete('filter.price');
      this.activeMaxPrice = null;
    } else {
      const inputs = parent.querySelectorAll('input');
      inputs.forEach((input) => {
        input.checked = false;
      });

      params.delete(`filter.${field}`);
      this.lastEditedFacet = null;
    }

    this.updateQuery(params);
    this.setLoadingState();
    this.getNostoSearchResults();
    this.getSearchResults();
  }

  renderNoResults() {
    if (!this.productGrid) return;

    const noResultsMessage = window.nostoStrings?.searchNoResults || 'No results for "{{terms}}"';
    const continueShoppingText = window.nostoStrings?.continueShopping || 'Continue shopping';
    this.classList.add('no-results');

    this.productGrid.innerHTML = `
          <div class="mt-gutter mb-gutter text-container">
            <p role="status">${noResultsMessage.replace('{{ terms }}', this.searchTerm || '')}</p>
            <a class="link" href="/">${continueShoppingText}</a>
          </div>
        `;
    this.removeLoadingState();

    // Update page title
    if (this.context === 'serp') {
      document.title = `Search: No results for "${this.searchTerm}"`;
    }
  }
}

if (!customElements.get('nosto-search')) {
  customElements.define('nosto-search', NostoSearch);
}
